import { IEntradaHistorialSuperEjecutivo, IEventoAplicadoSuperEjecutivo, IEventoSuperEjecutivo, IMensajeSuperEjecutivo } from './super-ejecutivo.interface';

/** Fin de un evento SSE. */
const FIN_DE_EVENTO = '\n\n';

/**
 * Separa los eventos SSE completos del texto recibido. Cada evento trae sus líneas `data:`; los
 * comentarios (`: latido`) y `[DONE]` se ignoran. Lo que no termina en línea en blanco queda en `resto`
 * para el siguiente pedazo.
 */
export function separarEventosSse(texto: string): { eventos: IEventoSuperEjecutivo[]; resto: string } {
  const normalizado = texto.replace(/\r\n/g, '\n');
  const partes = normalizado.split(FIN_DE_EVENTO);
  const resto = partes.pop() ?? '';
  const eventos: IEventoSuperEjecutivo[] = [];

  for (const parte of partes) {
    const datos = parte
      .split('\n')
      .filter((linea) => linea.startsWith('data:'))
      .map((linea) => linea.slice('data:'.length).trimStart())
      .join('\n');

    if (!datos || datos === '[DONE]') {
      continue;
    }

    try {
      eventos.push(JSON.parse(datos) as IEventoSuperEjecutivo);
    } catch {
      // Un evento ilegible no detiene la respuesta.
    }
  }

  return { eventos, resto };
}

/**
 * Aplica un evento a la respuesta del Super Ejecutivo en curso, sin modificarla. Cada parte de texto es un párrafo
 * aparte (igual que el historial que guarda el Super Ejecutivo); un error del stream se muestra como texto.
 */
export function aplicarEventoSuperEjecutivo(mensaje: IMensajeSuperEjecutivo, evento: IEventoSuperEjecutivo): IEventoAplicadoSuperEjecutivo {
  switch (evento.type) {
    case 'text-start':
      // Ya escribe la respuesta: el avance de la consulta anterior deja de aplicar (RN10).
      return { mensaje: { ...mensaje, texto: separarParrafo(mensaje.texto) }, avance: null };
    case 'text-delta':
      return { mensaje: { ...mensaje, texto: mensaje.texto + evento.delta } };
    case 'error':
      return { mensaje: { ...mensaje, texto: separarParrafo(mensaje.texto) + evento.errorText } };
    case 'data-propuesta':
      return {
        mensaje: { ...mensaje, propuestas: [...mensaje.propuestas, { ...evento.data, estado: 'pendiente' }] },
      };
    case 'data-conversacion':
      return { mensaje, conversationId: evento.data.conversationId };
    case 'data-avance':
      return { mensaje, avance: evento.data.mensaje };
    default:
      return { mensaje };
  }
}

/**
 * El Super Ejecutivo cerró la respuesta: la terminó (`finish`) o avisó que falló (`error`). Si el stream acaba sin
 * ninguno de los dos, se cortó la conexión y el Super Ejecutivo puede seguir respondiendo.
 */
export function cierraLaRespuestaSuperEjecutivo(evento: IEventoSuperEjecutivo): boolean {
  return evento.type === 'finish' || evento.type === 'error';
}

function separarParrafo(texto: string): string {
  return texto !== '' && !texto.endsWith('\n') ? `${texto}\n\n` : texto;
}

/** Marca las propuestas decididas (RN16) sin modificar los mensajes. */
export function marcarPropuestasSuperEjecutivo(
  mensajes: readonly IMensajeSuperEjecutivo[],
  decisiones: ReadonlyMap<string, boolean>,
): IMensajeSuperEjecutivo[] {
  return mensajes.map((m) =>
    m.propuestas.some((p) => decisiones.has(p.approvalId))
      ? {
          ...m,
          propuestas: m.propuestas.map((p) =>
            decisiones.has(p.approvalId)
              ? { ...p, estado: decisiones.get(p.approvalId) ? 'autorizada' : 'rechazada' }
              : p,
          ),
        }
      : m,
  );
}

/** Regresa a pendientes las propuestas cuya decisión no llegó al Super Ejecutivo (RN16). */
export function regresarAPendientesSuperEjecutivo(
  mensajes: readonly IMensajeSuperEjecutivo[],
  approvalIds: readonly string[],
): IMensajeSuperEjecutivo[] {
  const ids = new Set(approvalIds);

  return mensajes.map((m) =>
    m.propuestas.some((p) => ids.has(p.approvalId))
      ? { ...m, propuestas: m.propuestas.map((p) => (ids.has(p.approvalId) ? { ...p, estado: 'pendiente' } : p)) }
      : m,
  );
}

/**
 * La respuesta termina con el aviso de sesión expirada que escribe el Super Ejecutivo cuando Kobra le responde 401
 * a la mitad del turno (RN21). El texto es el mismo en los dos lados (`SESION_EXPIRADA`).
 */
export function terminaConSesionExpiradaSuperEjecutivo(texto: string, avisoSesionExpirada: string): boolean {
  return texto.trim().endsWith(avisoSesionExpirada);
}

/** Ids de las propuestas que esperan decisión. */
export function propuestasPendientesSuperEjecutivo(mensajes: readonly IMensajeSuperEjecutivo[]): string[] {
  return mensajes.flatMap((m) => m.propuestas.filter((p) => p.estado === 'pendiente').map((p) => p.approvalId));
}

/** RN17: escribir en lugar de contestar rechaza las propuestas pendientes. */
export function rechazarPendientesSuperEjecutivo(mensajes: readonly IMensajeSuperEjecutivo[]): IMensajeSuperEjecutivo[] {
  return marcarPropuestasSuperEjecutivo(mensajes, new Map(propuestasPendientesSuperEjecutivo(mensajes).map((id) => [id, false])));
}

/** Mensajes del panel a partir del historial que guarda el Super Ejecutivo (RN8.1). */
export function mensajesDesdeHistorialSuperEjecutivo(historial: readonly IEntradaHistorialSuperEjecutivo[]): IMensajeSuperEjecutivo[] {
  return historial.map((entrada, indice) => ({
    id: `historial-${indice}`,
    rol: entrada.rol,
    texto: entrada.texto,
    propuestas: entrada.propuestas.map((p) => ({ ...p })),
  }));
}
